import type { WorkCardModel, PlateTone } from '../primitives';
import type { WorkSummary } from '../api/types';
import { resolveSource } from '../api/sourceRegistry';
import { withBase } from '../paths';

/**
 * Adapter converting the API's package shape into the v2 `WorkCardModel` consumed by the
 * platform's `<WorkCard>` and `<WorkRow>` primitives.
 *
 * Several fields are derived rather than read straight off the API:
 *
 *   • `source`        - Work.source (brand id, e.g. "ko-fi"). Drives favicon / color via
 *                       the static source registry.
 *   • `sourceLabel`   - brand display name resolved through the registry (e.g. "Ko-Fi").
 *   • `canInstall`    - collapses the 4-way `InstallUrlKind` into a boolean.
 *   • `archived`      - true when the mirror summary records a Failed state.
 *   • `paywalled`     - true when `install.kind == 'Paywalled'`.
 *   • `noredist`      - always false until the term-detection signal is wired.
 *   • `creatorClaimed` - always false until the claim signal is wired.
 *   • `tone`          - derived deterministically from the package id.
 *   • `claimedByMe`   - server-projected per-user marker (ADR-0015); the raw
 *                       claimant ids never reach the public wire. False for anonymous.
 *   • `lifecycleStatus` - non-Published statuses surfaced ONLY when the viewer carries
 *                         the admin role. Drives the dashed lifecycle border.
 */

const TONE_ORDER: PlateTone[] = ['amber', 'violet', 'cool', 'rust', 'moss', 'fog', 'rose', 'azure', 'gold'];

const ISO_DATE_FORMAT = new Intl.DateTimeFormat('en', { year: 'numeric', month: 'short', day: 'numeric' });

/**
 * Build the long-form date tooltip surfaced on the card's date span.
 * Shapes:
 *   - both set     → "Created Apr 9, 2018 · Updated Mar 27, 2025"
 *   - updated only → "Updated Mar 27, 2025"
 *   - created only → "Created Apr 9, 2018"
 *   - neither     → undefined (no title attr → no tooltip)
 */
function formatUpdatedTooltip(createdAt: string | null | undefined, updatedAt: string | null | undefined): string | undefined {
    const created = formatIsoDate(createdAt);
    const updated = formatIsoDate(updatedAt);
    if (created && updated) return `Created ${created} · Updated ${updated}`;
    if (updated) return `Updated ${updated}`;
    if (created) return `Created ${created}`;
    return undefined;
}

function formatIsoDate(value: string | null | undefined): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return ISO_DATE_FORMAT.format(date);
}

interface AdapterContext {
    formatRelative?: (when: Date | string | number) => string;
    /**
     * Whether the viewer carries the admin role. Gates the lifecycle
     * border on the card - non-admins never see the dashed Suppressed
     * treatment regardless of the underlying Work status.
     */
    viewerIsAdmin?: boolean;
}

export function toCardModel(pkg: WorkSummary, ctx: AdapterContext = {}): WorkCardModel {
    const source = pkg.source ?? 'direct';
    const meta = resolveSource(source);

    // Server-projected per-user marker (ADR-0015); null/false for anonymous.
    const claimedByMe = pkg.claimedByMe ?? false;

    // Post ADR-0022 correction (A.6): the only admin-visible non-Published
    // Work status with card treatment is Suppressed. Tombstoned has its
    // own banner on the detail page rather than a card-border treatment.
    const lifecycleStatus = ctx.viewerIsAdmin && pkg.status === 'Suppressed'
        ? ('Suppressed' as const)
        : undefined;

    return {
        id: pkg.id,
        title: pkg.name ?? '(unnamed)',
        creator: pkg.author ?? 'Unknown',
        creatorClaimed: false,
        source,
        sourceLabel: meta.name,
        kind: pkg.kind,
        coverUrl: pkg.media.images.cover
            ? withBase(`/media/${encodeURIComponent(pkg.media.images.cover)}/package-card`)
            : null,
        tone: pickTone(pkg.id),
        likes: 0,
        // Card date semantics: "when the creator last touched it" beats "when
        // OUR crawler last saw a content-hash change." Prefer the upstream
        // updated date when we have it; fall back to the crawler clock for
        // scouts/sightings where it's still null. The tooltip surfaces
        // the long form (created + updated) so hover reveals the full
        // timeline without crowding the card.
        updatedLabel: ctx.formatRelative
            ? ctx.formatRelative(pkg.upstream.updatedAt ?? pkg.sightings.lastChangedAt ?? '')
            : undefined,
        updatedTooltip: formatUpdatedTooltip(pkg.upstream.createdAt, pkg.upstream.updatedAt),
        canInstall: pkg.installable && (pkg.install.kind === 'Mirror' || pkg.install.kind === 'Upstream'),
        paywalled: pkg.install.kind === 'Paywalled',
        noredist: false,
        claimedByMe,
        lifecycleStatus,
        publiclyListed: pkg.publiclyListed,
    };
}

function pickTone(id: string): PlateTone {
    let acc = 0;
    for (let i = 0; i < id.length; i += 1) acc = (acc + id.charCodeAt(i)) >>> 0;
    return TONE_ORDER[acc % TONE_ORDER.length];
}
